import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { ListingCard, ListingCardProps } from './ListingCard';
interface ListingCarouselProps {
  listings: ListingCardProps[];
}
export function ListingCarousel({
  listings
}: ListingCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, {
      passive: true
    });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [listings]);
  const scrollBy = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth'
    });
  };
  // Before the first scroll, the right control gets a soft ambient halo so the
  // sideways row is impossible to miss. It stops the moment the user moves.
  const showHint = !canScrollLeft && canScrollRight;
  const controlBase = 'hidden md:flex absolute top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-primary-indigo text-white ring-2 ring-white shadow-large hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-[background-color,transform,opacity] duration-150 ease-out';
  return <div className="relative group/carousel" data-id="element-2701" data-mp="1l7gd33w5gs6t">
      {/* Edge fades — soft signal that the row continues sideways */}
      <div className={`hidden md:block pointer-events-none absolute left-0 top-0 bottom-4 w-20 z-10 bg-gradient-to-r from-white via-white/80 to-transparent transition-opacity duration-200 ease-out ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`} data-id="element-2702" data-mp="2oi2nlqfgxx5j" />
      <div className={`hidden md:block pointer-events-none absolute right-0 top-0 bottom-4 w-20 z-10 bg-gradient-to-l from-white via-white/80 to-transparent transition-opacity duration-200 ease-out ${canScrollRight ? 'opacity-100' : 'opacity-0'}`} data-id="element-2703" data-mp="2t9kko3edfdl8" />

      {/* Left Control */}
      <button onClick={() => scrollBy('left')} aria-label="Previous listings" className={`${controlBase} -left-3 ${canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-2704" data-mp="20n9nl2pz3ut6">
        <ChevronLeftIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2705" data-mp="3pzl73vr9phzd" />
      </button>

      {/* Right Control */}
      <div className={`hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 transition-opacity duration-150 ease-out ${canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-2706" data-mp="3l19wvjx295gm">
        {showHint && <motion.span aria-hidden="true" className="absolute inset-0 rounded-full bg-primary-indigo/30" animate={{
        scale: [1, 1.5],
        opacity: [0.5, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        ease: 'easeOut'
      }} data-id="element-2707" data-mp="1fb2c6oh322p" />}
        <button onClick={() => scrollBy('right')} aria-label="Next listings" className="relative w-11 h-11 flex items-center justify-center rounded-full bg-primary-indigo text-white ring-2 ring-white shadow-large hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-[background-color,transform] duration-150 ease-out" data-id="element-2708" data-mp="l0nxculzsnet">
          <ChevronRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-2709" data-mp="13syv6a1shv62" />
        </button>
      </div>

      {/* Scroll Track */}
      <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-proximity no-scrollbar" data-id="element-2710" data-mp="3bctd0hdjg0j3">
        {listings.map(listing => <div key={listing.id} id={`listing-${listing.id}`} className="snap-start flex-shrink-0 w-[250px] sm:w-[280px] rounded-2xl" data-id="element-2711" data-mp="32attnif1wkcz">
            <ListingCard {...listing} data-id="element-2712" data-mp="3v012ba87yqby" />
          </div>)}
      </div>
    </div>;
}